import { supabase } from '../lib/supabase';
import { QuoteRequest, CreateQuoteRequest } from '../types/models';
import { Database } from '../types/database.types';

type QuoteRequestRow = Database['public']['Tables']['quote_requests']['Row'];
type QuoteRequestInsert = Database['public']['Tables']['quote_requests']['Insert'];

// Convert database row (snake_case) to application model (camelCase)
const convertToQuoteRequest = (row: QuoteRequestRow): QuoteRequest => ({
  id: row.id,
  quoteNumber: row.quote_number,
  serviceId: row.service_id,
  serviceName: row.service_name,
  propertyType: row.property_type,
  frequency: row.frequency,
  surfaceArea: row.surface_area,
  rooms: row.rooms,
  bathrooms: row.bathrooms,
  firstName: row.first_name,
  lastName: row.last_name,
  email: row.email,
  phone: row.phone,
  address: row.address,
  city: row.city,
  postalCode: row.postal_code,
  preferredDate: row.preferred_date,
  preferredTime: row.preferred_time,
  additionalNotes: row.additional_notes,
  basePrice: Number(row.base_price),
  frequencyMultiplier: Number(row.frequency_multiplier),
  estimatedPrice: Number(row.estimated_price),
  requestStatus: row.request_status,
  submissionMethod: row.submission_method,
  createdAt: row.created_at,
  updatedAt: row.updated_at
});

export const quoteRequestService = {
  // Get all quote requests (for admin dashboard)
  async getAll(): Promise<{ data: QuoteRequest[] | null; error: Error | null }> {
    try {
      const { data, error } = await supabase
        .from('quote_requests')
        .select('*')
        .order('created_at', { ascending: false });

      if (error) throw error;

      const quoteRequests = data?.map(convertToQuoteRequest) || [];
      return { data: quoteRequests, error: null };
    } catch (error) {
      return { data: null, error: error as Error };
    }
  },

  // Get a single quote request by ID
  async getById(id: string): Promise<{ data: QuoteRequest | null; error: Error | null }> {
    try {
      const { data, error } = await supabase
        .from('quote_requests')
        .select('*')
        .eq('id', id)
        .single();

      if (error) throw error;

      const quoteRequest = data ? convertToQuoteRequest(data) : null;
      return { data: quoteRequest, error: null };
    } catch (error) {
      return { data: null, error: error as Error };
    }
  },

  // Create a new quote request
  async create(quoteRequest: CreateQuoteRequest): Promise<{ data: QuoteRequest | null; error: Error | null }> {
    try {
      // Convert camelCase to snake_case for database
      const insertData: QuoteRequestInsert = {
        quote_number: quoteRequest.quoteNumber,
        service_id: quoteRequest.serviceId,
        service_name: quoteRequest.serviceName,
        property_type: quoteRequest.propertyType,
        frequency: quoteRequest.frequency,
        surface_area: quoteRequest.surfaceArea,
        rooms: quoteRequest.rooms,
        bathrooms: quoteRequest.bathrooms,
        first_name: quoteRequest.firstName,
        last_name: quoteRequest.lastName,
        email: quoteRequest.email,
        phone: quoteRequest.phone,
        address: quoteRequest.address || null,
        city: quoteRequest.city,
        postal_code: quoteRequest.postalCode || null,
        preferred_date: quoteRequest.preferredDate || null,
        preferred_time: quoteRequest.preferredTime || null,
        additional_notes: quoteRequest.additionalNotes || null,
        base_price: quoteRequest.basePrice,
        frequency_multiplier: quoteRequest.frequencyMultiplier,
        estimated_price: quoteRequest.estimatedPrice,
        submission_method: quoteRequest.submissionMethod || null,
        request_status: 'pending'
      };

      const { data, error } = await supabase
        .from('quote_requests')
        .insert(insertData)
        .select()
        .single();

      if (error) throw error;

      const createdQuote = data ? convertToQuoteRequest(data) : null;
      return { data: createdQuote, error: null };
    } catch (error) {
      return { data: null, error: error as Error };
    }
  },

  // Update quote request status
  async updateStatus(
    id: string, 
    status: 'pending' | 'contacted' | 'quoted' | 'accepted' | 'rejected' | 'completed'
  ): Promise<{ data: QuoteRequest | null; error: Error | null }> {
    try {
      const { data, error } = await supabase
        .from('quote_requests')
        .update({ request_status: status })
        .eq('id', id)
        .select()
        .single();

      if (error) throw error;

      const updatedQuote = data ? convertToQuoteRequest(data) : null;
      return { data: updatedQuote, error: null };
    } catch (error) {
      return { data: null, error: error as Error };
    }
  },

  // Delete a quote request
  async delete(id: string): Promise<{ error: Error | null }> {
    try {
      const { error } = await supabase
        .from('quote_requests')
        .delete()
        .eq('id', id);

      if (error) throw error;

      return { error: null };
    } catch (error) {
      return { error: error as Error };
    }
  }
};